import React from "react";
import classNames from "classnames";
import { Form, Input, InputGroup, SelectPicker } from "rsuite";
import { sanitizePhoneInput } from "../contactValidation";
import { getDialCodeFromPickerValue, getDialCodePickerValue } from "../phoneDialCodes";
import { OrderFormLocale } from "../types";
import FormSection from "./FormSection";
import { ORDER_FORM_FIELD_PROPS, PHONE_DIAL_CODE_PICKER_DATA } from "./orderFormRsuite";
import styles from "./orderForm.module.scss";

type PhoneFieldProps = {
  phoneDialCode: string;
  phone: string;
  onPhoneDialCodeChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  phonePlaceholder: string;
  dialCodeAria: string;
  onBlur?: () => void;
  error?: string;
};

const PhoneField: React.FC<PhoneFieldProps> = ({
  phoneDialCode,
  phone,
  onPhoneDialCodeChange,
  onPhoneChange,
  phonePlaceholder,
  dialCodeAria,
  onBlur,
  error,
}) => (
  <Form.Group controlId="contact-phone" className={styles.contactPhoneField}>
    <InputGroup
      size={ORDER_FORM_FIELD_PROPS.size}
      className={classNames(styles.contactPhoneInputGroup, {
        [styles.contactPhoneInputGroupInvalid]: Boolean(error),
      })}
    >
      <SelectPicker
        size={ORDER_FORM_FIELD_PROPS.size}
        appearance={ORDER_FORM_FIELD_PROPS.appearance}
        aria-label={dialCodeAria}
        className={styles.contactDialCodePicker}
        data={PHONE_DIAL_CODE_PICKER_DATA}
        value={getDialCodePickerValue(phoneDialCode) ?? undefined}
        onChange={(value) => onPhoneDialCodeChange(getDialCodeFromPickerValue(value))}
        renderValue={(value) => getDialCodeFromPickerValue(value)}
        cleanable={false}
        preventOverflow
        container={() => document.body}
        menuClassName={styles.contactDialCodeMenu}
        menuStyle={{ zIndex: 1100 }}
      />
      <Input
        className={styles.rsControl}
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        value={phone}
        onChange={(value) => onPhoneChange(sanitizePhoneInput(value))}
        onBlur={onBlur}
        placeholder={phonePlaceholder}
        aria-invalid={Boolean(error)}
      />
    </InputGroup>
    {error ? <span className={styles.contactFieldError}>{error}</span> : null}
  </Form.Group>
);

type ContactFieldsProps = {
  locale: OrderFormLocale;
  name: string;
  phoneDialCode: string;
  phone: string;
  phoneError?: string;
  onNameChange: (value: string) => void;
  onPhoneDialCodeChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onPhoneBlur?: () => void;
};

const ContactFields: React.FC<ContactFieldsProps> = ({
  locale,
  name,
  phoneDialCode,
  phone,
  phoneError,
  onNameChange,
  onPhoneDialCodeChange,
  onPhoneChange,
  onPhoneBlur,
}) => (
  <FormSection  title={locale.contact.title} controlId="contact-details" className={styles.contactSection}>
    <Form.Group controlId="contact-name" className={styles.contactNameGroup}>
      <Input
        {...ORDER_FORM_FIELD_PROPS}
        className={styles.rsControl}
        value={name}
        onChange={onNameChange}
        placeholder={locale.contact.namePlaceholder}
      />
    </Form.Group>

    <PhoneField
      phoneDialCode={phoneDialCode}
      phone={phone}
      onPhoneDialCodeChange={onPhoneDialCodeChange}
      onPhoneChange={onPhoneChange}
      phonePlaceholder={locale.contact.phonePlaceholder}
      dialCodeAria={locale.contact.dialCodeAria}
      onBlur={onPhoneBlur}
      error={phoneError}
    />
  </FormSection>
);

export default ContactFields;
